<template>
  <v-dialog
    :model-value="modelValue"
    max-width="600"
    transition="dialog-top-transition"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="cp-card">
      <v-text-field
        ref="input"
        v-model="q"
        autofocus
        hide-details
        variant="plain"
        density="comfortable"
        placeholder="Search tickets, projects, people…"
        prepend-inner-icon="mdi-magnify"
        class="cp-input px-3"
        @update:modelValue="onType"
        @keydown.esc="close"
      >
        <template v-slot:append-inner>
          <v-progress-circular v-if="loading" size="18" width="2" indeterminate color="primary"></v-progress-circular>
          <span v-else class="cp-kbd">ESC</span>
        </template>
      </v-text-field>

      <v-divider></v-divider>

      <div class="cp-results">
        <div v-if="q.length < 2" class="cp-hint">Type at least 2 characters to search.</div>
        <div v-else-if="!hasResults && !loading" class="cp-hint">No matches for “{{ q }}”.</div>

        <template v-else>
          <div v-if="results.tickets.length" class="cp-group">
            <div class="cp-group-label">Tickets</div>
            <div v-for="t in results.tickets" :key="'t' + t.ticket_id" class="cp-item" @click="select('ticket', t)">
              <v-icon size="18" class="cp-ic">mdi-ticket-outline</v-icon>
              <span class="cp-ref">{{ t.ticket_id }}</span>
              <span class="cp-title">{{ t.name }}</span>
              <span class="cp-tag">{{ t.status }}</span>
            </div>
          </div>

          <div v-if="results.it.length" class="cp-group">
            <div class="cp-group-label">IT Support</div>
            <div v-for="t in results.it" :key="'i' + t.ref" class="cp-item" @click="select('it', t)">
              <v-icon size="18" class="cp-ic">mdi-lifebuoy</v-icon>
              <span class="cp-ref">{{ t.ref }}</span>
              <span class="cp-title">{{ t.title }}</span>
              <span class="cp-tag">{{ t.status }}</span>
            </div>
          </div>

          <div v-if="results.projects.length" class="cp-group">
            <div class="cp-group-label">Projects</div>
            <div v-for="p in results.projects" :key="'p' + p.id" class="cp-item" @click="select('project', p)">
              <v-icon size="18" class="cp-ic">mdi-folder-outline</v-icon>
              <span class="cp-ref">{{ p.key }}</span>
              <span class="cp-title">{{ p.title }}</span>
            </div>
          </div>

          <div v-if="results.users.length" class="cp-group">
            <div class="cp-group-label">People</div>
            <div v-for="u in results.users" :key="'u' + u.id" class="cp-item" @click="select('user', u)">
              <v-icon size="18" class="cp-ic">mdi-account-outline</v-icon>
              <span class="cp-title">{{ u.fullname }}</span>
              <span class="cp-tag">{{ u.team }}</span>
            </div>
          </div>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import apiMixin from '@/mixins/apiMixin';

export default {
  name: 'CommandPalette',
  mixins: [apiMixin],
  props: { modelValue: { type: Boolean, default: false } },
  emits: ['update:modelValue'],
  data() {
    return {
      constant,
      q: '',
      loading: false,
      timer: null,
      results: { tickets: [], it: [], projects: [], users: [] },
    };
  },
  computed: {
    hasResults() {
      const r = this.results;
      return r.tickets.length || r.it.length || r.projects.length || r.users.length;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.q = '';
        this.results = { tickets: [], it: [], projects: [], users: [] };
      }
    },
  },
  methods: {
    onType() {
      clearTimeout(this.timer);
      if (this.q.trim().length < 2) {
        this.results = { tickets: [], it: [], projects: [], users: [] };
        return;
      }
      this.timer = setTimeout(this.doSearch, 250);
    },
    doSearch() {
      this.loading = true;
      axios.get(utils._api(this.constant.global_search), { ...this.authHeaders(), params: { q: this.q.trim() } })
        .then((response) => {
          const d = response.data.data || {};
          this.results = {
            tickets: d.tickets || [],
            it: d.it || [],
            projects: d.projects || [],
            users: d.users || [],
          };
        })
        .catch((error) => console.error('Search error:', error))
        .finally(() => { this.loading = false; });
    },
    goto(nav) {
      window.dispatchEvent(new CustomEvent('int:navigate', { detail: { nav } }));
      this.close();
    },
    select(type, item) {
      if (type === 'ticket') {
        // Prefill the All Tickets search so the ticket is easy to spot.
        try { localStorage.setItem('int_search_ticket', item.ticket_id); } catch (e) { /* ignore */ }
        this.goto('All Project Tickets');
      } else if (type === 'it') {
        this.goto('IT Support');
      } else if (type === 'project') {
        this.goto('Projects');
      } else if (type === 'user') {
        this.goto('User Management');
      }
    },
    close() {
      this.$emit('update:modelValue', false);
    },
  },
};
</script>

<style scoped>
.cp-card { border-radius: var(--int-radius-lg); overflow: hidden; }
.cp-input :deep(input) { font-size: 1.05rem; }
.cp-kbd {
  font-size: 0.62rem;
  font-weight: 700;
  color: var(--int-text-faint);
  border: 1px solid var(--int-border);
  border-radius: 4px;
  padding: 1px 5px;
}
.cp-results { max-height: 60vh; overflow-y: auto; padding: 6px 0; }
.cp-hint { padding: 22px; text-align: center; color: var(--int-text-faint); font-size: 0.88rem; }
.cp-group { padding: 4px 0; }
.cp-group-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--int-text-faint);
  padding: 6px 16px 2px;
}
.cp-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  cursor: pointer;
}
.cp-item:hover { background: var(--int-surface-muted); }
.cp-ic { color: var(--int-text-muted); flex: 0 0 auto; }
.cp-ref { font-weight: 700; font-size: 0.8rem; color: var(--int-text-strong); flex: 0 0 auto; }
.cp-title { font-size: 0.88rem; color: var(--int-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cp-tag {
  margin-left: auto;
  font-size: 0.7rem;
  color: var(--int-text-muted);
  background: var(--int-surface-muted);
  border-radius: 6px;
  padding: 1px 7px;
  flex: 0 0 auto;
}
</style>
